import React, { useState, useEffect } from 'react';

interface TimeSlotSelectorProps {
  startTime: string; // Format: "HH:MM"
  endTime: string;   // Format: "HH:MM"
  eventType: "VIP" | "PUBLIC";   // Format: "HH:MM"
  selectedSlot: string | null;
  onChange: (slot: string) => void;
}

export const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({
  startTime,
  endTime,
  selectedSlot,
  onChange,
  eventType
}) => {
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAvailableSlots = async () => {
      try {
        setLoading(true);
        const response = await fetch(`https://api.regenphd.com/regenpod-slots?eventType=${eventType}`);
        const data = await response.json();
        
        // Count bookings per slot
        const slotCounts: { [key: string]: number } = {};
        data.forEach((booking: { timeSlot: string }) => {
          slotCounts[booking.timeSlot] = (slotCounts[booking.timeSlot] || 0) + 1;
        });
        
        // Generate all possible slots
        const allSlots = generateTimeSlots();
        
        // Filter slots with less than 2 bookings
        const available = allSlots.filter(slot => (slotCounts[slot] || 0) < 2);
        
        setAvailableSlots(available);
        setError(available.length === 0 ? 'No more slots available for this session' : null);
      } catch (err) {
        setError('Failed to load available slots');
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableSlots();
  }, [eventType, startTime, endTime]);

  const generateTimeSlots = (): string[] => {
    const slots: string[] = [];
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    
    for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
      const hour = Math.floor(minutes / 60);
      const min = minutes % 60;
      const timeStr = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      slots.push(timeStr);
    }
    
    return slots;
  };

  const formatDisplayTime = (time: string): string => {
    const [hour, min] = time.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${min.toString().padStart(2, '0')} ${period}`;
  };

  return (
    <div className="time-slot-selector">
      <div className="slot-header">
        <h3 className="slot-title font-medium">Schedule Your Pod Session</h3>
        <p className="slot-subtitle">Select your preferred time slot (optional)</p>
      </div>
      {loading ? (
        <div className="loading-message">Loading available slots...</div>
      ) : error ? (
        <div className="no-slots-container">
          <div className="no-slots-icon">⏰</div>
          <h4 className="no-slots-title font-medium">All Sessions Fully Booked</h4>
          <p className="no-slots-message">We're sorry, but all available time slots for this session have been reserved. Please check back later or contact us for updates.</p>
        </div>
      ) : (
        <div className="time-slots-grid">
          {availableSlots.map((slot) => (
            <button
              key={slot}
              type="button"
              className={`time-slot-btn ${selectedSlot === slot ? 'selected' : ''}`}
              onClick={() => onChange(slot)}
            >
              {formatDisplayTime(slot)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};